import React, { useMemo, useState, useEffect } from 'react'
import {
  useHasReceptor,
  useReceptor,
} from 'src/pages/Receptors/receptors.hooks'
import { useIsShowModal, useHideModal } from 'src/Modal/ModalStateContext'
import { ModalForm, ModalFormId } from 'src/components/ModalForms/ModalForm'
import { useControlModel } from 'src/components/async/model/control/useControl'
import { SelectDropdown } from 'src/components/Reusable/SelectDropdown'
import type { SelectItem } from 'src/components/Reusable/SelectDropdown/SelectDropdown.constants'
import { TableListLoader } from 'src/components/Reusable/ContentLoaders/TableListLoader'
import {
  useGetWorkflowCredentials,
  WorkflowCredentials,
} from './AddWorkflowEvidence.hooks'
import {
  WorkflowFormState,
  AddWorkflowEvidenceForm,
  Option,
} from './forms/AddWorkflowEvidenceForm'
import { AddWorkflowEvidenceFormDescription } from './forms/AddWorkflowEvidenceForm.components'
import { FormBody } from './forms/AddWorkflowEvidenceForm.styles'
import {
  WORKFLOW_SYSTEM_NAMES,
  WorkflowSystemId,
} from './AddWorkflowEvidence.constants'

type AddWorkflowEvidenceModalProps = {
  controlId?: string
  requestId?: string
  name: string
  openOnDrag?: boolean
  description?: string
} & (
  | { requestId: string; controlId?: never }
  | { controlId: string; requestId?: never }
)

export const AddWorkflowEvidenceModal = ({
  controlId,
  requestId,
  name,
  openOnDrag = false,
  description,
}: AddWorkflowEvidenceModalProps): JSX.Element => {
  const show = useIsShowModal(ModalFormId.ADD_WORKFLOW_EVIDENCE)
  const hide = useHideModal({
    modalId: ModalFormId.ADD_WORKFLOW_EVIDENCE,
    onHide: () => {
      setSelectedWorkflow('')
    },
  })

  // These are retained for future use incase we need backend calls
  const _placeholderName = name
  const _placeholderReqest = requestId

  // Selected workflow (set early so hooks below can depend on it)
  const [selectedWorkflow, setSelectedWorkflow] = useState<string>('')

  const { data: HasReceptor, isLoading: isLoadingHasJiraReceptor } =
    useHasReceptor('trr-jira-workflow', true)
  const hasJiraWorkflowReceptor = useMemo(
    () => HasReceptor?.getHasReceptor() || false,
    [HasReceptor],
  )
  const { data: receptor, isLoading: isJiraWorkflowReceptorLoading } =
    useReceptor({
      modelId: 'trr-jira-workflow',
      shouldFetch: hasJiraWorkflowReceptor,
    })
  const { wfCredentials } = useGetWorkflowCredentials(selectedWorkflow)

  const { data: HasSnowReceptor, isLoading: isLoadingHasSnowReceptor } =
    useHasReceptor('trr-servicenow-workflow', true)

  const hasSnowWorkflowReceptor = useMemo(
    () => HasSnowReceptor?.getHasReceptor() || false,
    [HasSnowReceptor],
  )

  const { data: snowReceptor, isLoading: isSnowWorkflowReceptorLoading } =
    useReceptor({
      modelId: 'trr-servicenow-workflow',
      shouldFetch: hasSnowWorkflowReceptor,
    })

  const isLoading = useMemo(
    () =>
      isJiraWorkflowReceptorLoading ||
      isSnowWorkflowReceptorLoading ||
      isLoadingHasJiraReceptor ||
      isLoadingHasSnowReceptor,
    [
      isJiraWorkflowReceptorLoading,
      isSnowWorkflowReceptorLoading,
      isLoadingHasJiraReceptor,
      isLoadingHasSnowReceptor,
    ],
  )

  const hasMultipleWorkflows =
    hasSnowWorkflowReceptor && hasJiraWorkflowReceptor

  const workflowItems: SelectItem[] = useMemo(() => {
    const list: SelectItem[] = []
    if (hasJiraWorkflowReceptor) {
      list.push({ name: 'Jira', value: 'trr-jira-workflow' })
    }
    if (hasSnowWorkflowReceptor) {
      list.push({ name: 'ServiceNow', value: 'trr-servicenow-workflow' })
    }
    return list
  }, [hasJiraWorkflowReceptor, hasSnowWorkflowReceptor])

  const workflowDefaultValues: SelectItem[] = useMemo(() => {
    if (!selectedWorkflow) return []
    const name =
      selectedWorkflow === 'trr-jira-workflow' ? 'Jira' : 'ServiceNow'
    return [{ name, value: selectedWorkflow }]
  }, [selectedWorkflow])

  useEffect(() => {
    // Only auto-select when nothing is chosen yet AND exactly one receptor is enabled
    if (selectedWorkflow || isLoading) return

    if (hasJiraWorkflowReceptor && !hasSnowWorkflowReceptor) {
      setSelectedWorkflow('trr-jira-workflow')
    } else if (!hasJiraWorkflowReceptor && hasSnowWorkflowReceptor) {
      setSelectedWorkflow('trr-servicenow-workflow')
    }
  }, [
    hasJiraWorkflowReceptor,
    hasSnowWorkflowReceptor,
    selectedWorkflow,
    isLoading,
  ])

  // Derived state for workflow-specific defaults
  const [workflowSiteUrl, setWorkflowSiteUrl] = useState<string>('')
  const [workflowLabel, setWorkflowLabel] = useState<string>('')

  // Normalize credentials shape (supports both Site/Label and site/label)
  const resolveCreds = (cred: WorkflowCredentials) => {
    const site = (cred?.Site ?? cred?.site ?? '').trim()
    const label = (cred?.Label ?? cred?.label ?? '').trim()
    return { site, label }
  }

  useEffect(() => {
    const { site, label } = resolveCreds(wfCredentials)
    setWorkflowSiteUrl(site)
    setWorkflowLabel(label)
  }, [selectedWorkflow, wfCredentials])

  // Submit button text as state, reacts to workflow selection
  const [submitText, setSubmitText] = useState<string>('Continue')
  useEffect(() => {
    const systemName =
      WORKFLOW_SYSTEM_NAMES[selectedWorkflow as WorkflowSystemId] || ''
    if (systemName) {
      setSubmitText(`Continue in ${systemName}`)
    } else {
      setSubmitText('Continue')
    }
  }, [selectedWorkflow])

  const { data: control } = useControlModel(controlId ?? '')
  const resolvedControlId = useMemo(
    () => control?.getModelId?.() || controlId || '',
    [control, controlId],
  )

  const configToOptions = (configRaw?: string): Option[] => {
    try {
      const cfg = (
        typeof configRaw === 'string' ? JSON.parse(configRaw) : configRaw ?? {}
      ) as {
        fetch_sources?: { project_mappings?: Record<string, string>[] }
      }
      const raw = cfg.fetch_sources?.project_mappings ?? []
      return raw.map((pm) => ({
        name: pm.name ?? '',
        pid: pm.pid ?? '',
        pname: pm.pname ?? '',
        tid: pm.tid ?? '',
        tname: pm.tname ?? '',
        tpid: pm.tpid ?? '',
      }))
    } catch {
      return []
    }
  }

  type AdvanceMapping = {
    echoice_id?: string
    echoice_name?: string
    efield_id?: string
    efield_name?: string
    name?: string
    pid?: string
    pname?: string
    sfield_id?: string
    sfield_name?: string
    tid?: string
    tname?: string
    tpid?: string
  }

  type ConfigShape = {
    fetch_sources?: {
      advance_mappings?: AdvanceMapping[]
    }
  }

  const getTitleFieldFromAdvanceMappings = (
    configRaw?: string | unknown,
    tableId?: string,
  ): string | undefined => {
    try {
      const cfg: ConfigShape =
        (typeof configRaw === 'string' ? JSON.parse(configRaw) : configRaw) ??
        {}

      const mappings = cfg.fetch_sources?.advance_mappings
      if (!Array.isArray(mappings) || mappings.length === 0) return undefined

      const normTable = (tableId ?? '').trim().toLowerCase()

      // Find mapping for the specified tableId; if none provided, use first row
      const match =
        mappings.find(
          (m) => (m.tid ?? '').trim().toLowerCase() === normTable,
        ) ?? (normTable ? undefined : mappings[0])

      if (!match) return undefined

      // Prefer the API field id; fall back to display name if id missing
      const titleField = match.sfield_id?.trim()
      return titleField || undefined
    } catch {
      return undefined
    }
  }

  const activeReceptor = useMemo(() => {
    if (selectedWorkflow === 'trr-servicenow-workflow') return snowReceptor
    if (selectedWorkflow === 'trr-jira-workflow') return receptor
    return undefined
  }, [selectedWorkflow, receptor, snowReceptor])
  const selectionOptons = configToOptions(activeReceptor?.getConfig?.())

  const onSubmitFunc = async (data: WorkflowFormState) => {
    const { project, taskType, title, description } = data
    if (selectedWorkflow === 'trr-servicenow-workflow') {
      // Build ServiceNow new-record URL using credentials (no Jira fallback)
      const inst = (workflowSiteUrl || '').trim()

      // Extract host (strip scheme/path if provided)
      let host = inst
      if (/^https?:\/\//i.test(host)) {
        host = host.replace(/^https?:\/\/(.*?)(\/.*)?$/i, '$1')
      }

      // Table from taskType when provided, else 'task'
      const table = data.taskType || 'task'

      // Label must come from ServiceNow credentials per spec
      const credLabel = (workflowLabel || '').trim()
      // Inner URI to encode for nav_to.do; enforce double-encoding of '^' to %255E as in example
      const advTitleField = getTitleFieldFromAdvanceMappings(
        activeReceptor?.getConfig(),
        table,
      )
      const fieldForTitle =
        advTitleField && advTitleField.trim()
          ? advTitleField.trim()
          : 'short_description'
      const inner = `${table}.do?sys_id=-1&sysparm_query=${fieldForTitle}=${title}^comments=[${credLabel}]`
      let encoded = encodeURIComponent(inner)
      encoded = encoded.replace(/%5E/g, '%255E')

      const url = `https://${host}/nav_to.do?uri=${encoded}`
      window.open(url, '_blank', 'noopener,noreferrer')
      hide()
      return
    }

    // Default: Jira
    let site = (workflowSiteUrl || '').trim()
    if (site && !/^https?:\/\//i.test(site)) site = `https://${site}`

    const base = `${site.replace(
      /\/$/,
      '',
    )}/secure/CreateIssueDetails!init.jspa`

    const params = new URLSearchParams()
    if (project) params.set('pid', String(project))
    if (taskType) params.set('issuetype', String(taskType))
    if (title) params.set('summary', title)
    if (description) params.set('description', description)
    if (workflowLabel) params.set('labels', workflowLabel)

    const url = `${base}?${params.toString()}`
    window.open(url, '_blank', 'noopener,noreferrer')

    hide() // close the modal after launching
  }

  return (
    <ModalForm
      formId={ModalFormId.ADD_WORKFLOW_EVIDENCE}
      title={'Create a Workflow Task for Evidence'}
      description={<AddWorkflowEvidenceFormDescription />}
      openOnDrag={openOnDrag}
      submitText={submitText}
      show={show}
      hide={hide}
      enforceFocus={false}
      showOverflow
      disableSubmitButton={!selectionOptons.length}
    >
      {hasMultipleWorkflows && (
        <FormBody>
          <SelectDropdown
            label="Workflow Integration"
            items={workflowItems}
            defaultValues={workflowDefaultValues}
            onSelectCb={([item]) =>
              setSelectedWorkflow(String(item.value ?? ''))
            }
            placeholder="Select workflow"
            hideError
          />
        </FormBody>
      )}
      {isLoading ? (
        <FormBody>
          <TableListLoader rows={1} />
        </FormBody>
      ) : selectedWorkflow ? (
        <AddWorkflowEvidenceForm
          modalId={ModalFormId.ADD_WORKFLOW_EVIDENCE}
          activeReceptor={activeReceptor}
          onSubmitFunc={onSubmitFunc}
          isShown={show}
          controlId={resolvedControlId}
          siteUrl={workflowSiteUrl}
          label={workflowLabel}
          name={WORKFLOW_SYSTEM_NAMES[selectedWorkflow as WorkflowSystemId]}
          configs={selectionOptons}
          description={description || ''}
        />
      ) : null}
    </ModalForm>
  )
}
